import { Spin } from "antd";
import type { LineConfig } from "./LineChartCard";
import styles from "./SectorStrategicMapCard.module.css";

export interface SectorStrategicMapCardProps {
  title: string;
  data: Record<string, string | number>[];
  xKey: string;
  series: LineConfig[];
  loading?: boolean;
  error?: string | null;
  headerExtra?: React.ReactNode;
}

interface SectorRow {
  key: string;
  label: string;
  color: string;
  firstValue: number;
  lastValue: number;
  delta: number;
}

interface SectorStatus {
  label: string;
  className: string;
}

interface PositionedSector extends SectorRow {
  x: number;
  y: number;
  size: number;
}

const getNumericValue = (value: string | number | undefined) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatValue = (value: number) => {
  return Number.isInteger(value) ? value.toString() : value.toFixed(1).replace(/\.0$/, "");
};

/** Fracció del diàmetre usable per al text en una sola línia al centre del cercle. */
const BUBBLE_FIT_WIDTH_RATIO = 0.66;
/** Inter 900: amplada aproximada per caràcter en funció de font-size (conservador). */
const BUBBLE_CHAR_WIDTH_RATIO = 0.64;
const BUBBLE_VALUE_FONT_MIN = 8;
const BUBBLE_VALUE_FONT_MAX = 17;

/** Diàmetre mínim perquè `formatValue` càpiga sense retallar (sense puntets). */
const minBubbleDiameterForValueText = (lastValue: number) => {
  const digits = formatValue(lastValue);
  const n = Math.max(1, digits.length);
  const minD =
    (BUBBLE_VALUE_FONT_MIN * BUBBLE_CHAR_WIDTH_RATIO * n) / BUBBLE_FIT_WIDTH_RATIO + 8;
  return Math.ceil(minD);
};

/** Font-size que fa caber el text dins el cercle; mai retalla amb ellipsis. */
const bubbleValueFontSizePx = (diameterPx: number, lastValue: number) => {
  const digits = formatValue(lastValue);
  const n = Math.max(1, digits.length);
  const maxW = diameterPx * BUBBLE_FIT_WIDTH_RATIO;
  const maxFont = maxW / (n * BUBBLE_CHAR_WIDTH_RATIO);
  return Math.round(
    Math.min(BUBBLE_VALUE_FONT_MAX, Math.max(BUBBLE_VALUE_FONT_MIN, maxFont))
  );
};

const formatDelta = (value: number) => {
  const sign = value > 0 ? "+" : "";
  const formattedValue = Math.abs(value) < 0.05
    ? "0"
    : value.toFixed(1).replace(/\.0$/, "");

  return `${sign}${formattedValue}`;
};

const buildRows = (
  data: Record<string, string | number>[],
  series: LineConfig[]
): SectorRow[] => {
  return series
    .map(({ key, label, color }) => {
      const values = data
        .map((point) => getNumericValue(point[key]))
        .filter((value): value is number => value !== null);

      if (values.length === 0) return null;

      const firstValue = values[0];
      const lastValue = values[values.length - 1];

      return {
        key,
        label,
        color,
        firstValue,
        lastValue,
        delta: lastValue - firstValue,
      };
    })
    .filter((row): row is SectorRow => row !== null);
};

const clamp = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(min, value));
};

const getDeltaClass = (value: number) => {
  if (value > 0.05) return styles.positive;
  if (value < -0.05) return styles.negative;
  return styles.flat;
};

const getMedianValue = (values: number[]) => {
  if (values.length === 0) return 0;

  const sortedValues = [...values].sort((a, b) => a - b);
  const middleIndex = Math.floor(sortedValues.length / 2);

  if (sortedValues.length % 2 === 1) return sortedValues[middleIndex];

  return (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2;
};

const getStrategicCoordinates = (
  row: SectorRow,
  maxLastValue: number,
  maxAbsDelta: number
) => {
  const weightRatio = maxLastValue > 0 ? row.lastValue / maxLastValue : 0.5;
  const momentumRatio = maxAbsDelta > 0 ? row.delta / maxAbsDelta : 0;

  return {
    x: clamp(14 + Math.sqrt(weightRatio) * 69, 14, 83),
    y: clamp(50 - momentumRatio * 32, 18, 82),
  };
};

const getPositionedRows = (
  rows: SectorRow[],
  maxLastValue: number,
  maxAbsDelta: number
): PositionedSector[] => {
  const maxBubbleDiameter = 94;
  /** Pis visual per àrea; el diàmetre efectiu no pot ser menor que el text complet. */
  const minBubbleDiameterArea = 26;
  const maxBubbleArea = Math.PI * Math.pow(maxBubbleDiameter / 2, 2);

  const getBubbleDiameterFromArea = (value: number) => {
    if (maxLastValue <= 0 || value <= 0) return minBubbleDiameterArea;

    // Fem que l'ÀREA de la bombolla sigui proporcional al valor real:
    // això permet una lectura visual fidel de magnituds.
    const area = (value / maxLastValue) * maxBubbleArea;
    const diameter = 2 * Math.sqrt(area / Math.PI);

    return clamp(diameter, minBubbleDiameterArea, maxBubbleDiameter);
  };

  const positionedRows = rows.map((row) => {
    const coordinates = getStrategicCoordinates(row, maxLastValue, maxAbsDelta);
    const fromArea = getBubbleDiameterFromArea(row.lastValue);
    const fromText = minBubbleDiameterForValueText(row.lastValue);
    const size = clamp(Math.max(fromArea, fromText), minBubbleDiameterArea, maxBubbleDiameter);
    const edgeMargin = 6 + (size / maxBubbleDiameter) * 3;
    const boundedX = clamp(coordinates.x, 14 + edgeMargin, 83 - edgeMargin);
    const boundedY = clamp(coordinates.y, 18 + edgeMargin, 82 - edgeMargin);

    return {
      ...row,
      x: boundedX,
      y: boundedY,
      size,
    };
  });

  const closeRows = positionedRows.filter((row) => row.x < 60);
  const sortedCloseRows = [...closeRows].sort((a, b) => a.x - b.x);
  const offsets = [-9, 0, 9, 16];

  sortedCloseRows.forEach((row, index) => {
    const offset = offsets[index % offsets.length];
    row.x = clamp(row.x + offset, 14, 83);
  });

  return positionedRows;
};

const getSectorStatus = (row: SectorRow, medianValue: number): SectorStatus => {
  const hasHighWeight = row.lastValue >= medianValue;

  if (hasHighWeight && row.delta > 0.05) {
    return { label: "Motor principal", className: styles.statusMotor };
  }

  if (!hasHighWeight && row.delta > 0.05) {
    return { label: "Emergent", className: styles.statusEmergent };
  }

  if (hasHighWeight && row.delta < -0.05) {
    return { label: "Pes en retrocés", className: styles.statusDecline };
  }

  if (row.delta < -0.05) {
    return { label: "A vigilar", className: styles.statusCaution };
  }

  return { label: "Estable", className: styles.statusStable };
};

const SectorStrategicMapCard = ({
  title,
  data,
  xKey,
  series,
  loading = false,
  error = null,
  headerExtra,
}: SectorStrategicMapCardProps) => {
  const rows = buildRows(data, series);
  const firstPeriod = data[0]?.[xKey];
  const lastPeriod = data[data.length - 1]?.[xKey];
  const sortedRows = [...rows].sort((a, b) => b.lastValue - a.lastValue);
  const leader = sortedRows[0];
  const secondSector = sortedRows[1];
  const strongestGrowth = [...rows].sort((a, b) => b.delta - a.delta)[0];
  const weakestMomentum = [...rows].sort((a, b) => a.delta - b.delta)[0];
  const maxLastValue = rows.length > 0 ? Math.max(...rows.map((row) => row.lastValue)) : 0;
  const medianLastValue = getMedianValue(rows.map((row) => row.lastValue));
  const deltas = rows.map((row) => row.delta);
  const maxAbsDelta = Math.max(1, ...deltas.map((delta) => Math.abs(delta)));
  const positionedRows = getPositionedRows(rows, maxLastValue, maxAbsDelta);
  const zeroLine = 50;
  const leaderMultiple = leader && secondSector && secondSector.lastValue !== 0
    ? leader.lastValue / secondSector.lastValue
    : null;

  return (
    <div className={styles.card} data-chart-card>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>
            Mapa impacte-moviment: pes actual i canvi entre {firstPeriod ?? "inici"} i {lastPeriod ?? "final"}
          </p>
        </div>
        {headerExtra}
      </div>

      {loading && (
        <div className={styles.centered}>
          <Spin size="small" /> Carregant dades…
        </div>
      )}
      {!loading && error && (
        <div className={styles.centered}>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div className={styles.centered}>
          <p className={styles.emptyMessage}>Sense dades disponibles</p>
        </div>
      )}
      {!loading && !error && rows.length > 0 && (
        <>
          <div className={styles.insights}>
            {leader && (
              <div className={styles.insight}>
                <span className={styles.insightLabel}>Sector dominant</span>
                <strong>{leader.label}</strong>
                <span>
                  {formatValue(leader.lastValue)} ocupats
                  {leaderMultiple ? ` · ${leaderMultiple.toFixed(1)}x vs ${secondSector?.label}` : ""}
                </span>
              </div>
            )}
            {strongestGrowth && (
              <div className={styles.insight}>
                <span className={styles.insightLabel}>Major impuls</span>
                <strong>{strongestGrowth.label}</strong>
                <span className={getDeltaClass(strongestGrowth.delta)}>
                  {formatDelta(strongestGrowth.delta)} des de {firstPeriod ?? "inici"}
                </span>
              </div>
            )}
            {weakestMomentum && (
              <div className={styles.insight}>
                <span className={styles.insightLabel}>Punt a vigilar</span>
                <strong>{weakestMomentum.label}</strong>
                <span className={getDeltaClass(weakestMomentum.delta)}>
                  {formatDelta(weakestMomentum.delta)} des de {firstPeriod ?? "inici"}
                </span>
              </div>
            )}
          </div>

          <div className={styles.mapLayout}>
            <div className={styles.map}>
              <span className={styles.axisLabelTop}>Més impuls</span>
              <span className={styles.axisLabelBottom}>Menys impuls</span>
              <span className={styles.axisLabelLeft}>Menys pes actual</span>
              <span className={styles.axisLabelRight}>Més pes actual</span>

              <div className={styles.quadrantTopLeft}>Emergent</div>
              <div className={styles.quadrantTopRight}>Motor</div>
              <div className={styles.quadrantBottomLeft}>Residual</div>
              <div className={styles.quadrantBottomRight}>Dominant en retrocés</div>
              <span className={styles.zeroLine} style={{ top: `${zeroLine}%` }} />

              {positionedRows.map((row) => {
                const sectorStatus = getSectorStatus(row, medianLastValue);

                return (
                  <div
                    key={row.key}
                    className={styles.bubbleGroup}
                    style={{
                      left: `${row.x}%`,
                      top: `${row.y}%`,
                    }}
                  >
                    <span className={styles.bubbleLabel}>{row.label}</span>
                    <div
                      className={styles.bubble}
                      style={{
                        width: row.size,
                        height: row.size,
                        borderColor: row.color,
                        backgroundColor: `${row.color}26`,
                      }}
                    >
                      <span
                        className={styles.bubbleValue}
                        style={{
                          fontSize: `${bubbleValueFontSizePx(row.size, row.lastValue)}px`,
                        }}
                      >
                        {formatValue(row.lastValue)}
                      </span>
                    </div>
                    <span className={`${styles.bubbleDelta} ${getDeltaClass(row.delta)}`}>
                      {formatDelta(row.delta)}
                    </span>
                    <span
                      className={`${styles.bubbleStatus} ${sectorStatus.className}`}
                    >
                      {sectorStatus.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className={styles.decisionPanel}>
              <h3>Lectura accionable</h3>
              {sortedRows.map((row) => {
                const sectorStatus = getSectorStatus(row, medianLastValue);

                return (
                  <div className={styles.decisionItem} key={row.key}>
                    <span className={styles.dot} style={{ backgroundColor: row.color }} />
                    <div>
                      <div className={styles.decisionHeader}>
                        <strong>{row.label}</strong>
                        <span className={sectorStatus.className}>
                          {sectorStatus.label}
                        </span>
                      </div>
                      <span>
                        {formatValue(row.lastValue)} ocupats · <span className={getDeltaClass(row.delta)}>{formatDelta(row.delta)}</span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SectorStrategicMapCard;
